import { Link } from "react-router-dom";
import { Phone, Mail, ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/data/contentEngine";

export const SectionHead = ({ eyebrow, title, sub, testId, align = "left" }) => (
  <Reveal className={align === "center" ? "text-center" : ""}>
    <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
      {eyebrow}
    </p>
    <h2
      className="mt-3 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-slate-100 sm:text-4xl"
      data-testid={testId}
    >
      {title}
    </h2>
    {sub ? (
      <p className={`mt-4 max-w-2xl text-base leading-relaxed text-slate-400 ${align === "center" ? "mx-auto" : ""}`}>
        {sub}
      </p>
    ) : null}
  </Reveal>
);

export const FaqBlock = ({ faqs, testId }) => (
  <Accordion type="single" collapsible className="w-full" data-testid={testId}>
    {faqs.map((f, i) => (
      <AccordionItem
        key={i}
        value={`faq-${i}`}
        className="border-slate-800"
        data-testid={`${testId}-item-${i}`}
      >
        <AccordionTrigger className="text-left font-display text-base font-semibold uppercase tracking-wide text-slate-200 hover:text-amber-400 hover:no-underline">
          {f.q}
        </AccordionTrigger>
        <AccordionContent className="text-sm leading-relaxed text-slate-400">
          {f.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="breadcrumbs">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-1">
        {i > 0 ? <ChevronRight className="h-3 w-3 text-slate-600" /> : null}
        {it.to ? (
          <Link to={it.to} className="transition-colors hover:text-amber-400" data-testid={`breadcrumb-${i}`}>
            {it.label}
          </Link>
        ) : (
          <span className="text-slate-300" data-testid={`breadcrumb-${i}`}>{it.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const CtaBand = ({ title, sub }) => (
  <Reveal>
    <div
      className="relative overflow-hidden rounded-md border border-amber-500/30 bg-obsidian-900 px-6 py-12 text-center sm:px-12"
      data-testid="cta-band"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 50% 120%, rgba(245,158,11,0.5), transparent)",
        }}
      />
      <h2 className="relative font-display text-3xl font-bold uppercase tracking-tight text-slate-100 sm:text-4xl">
        {title}
      </h2>
      <p className="relative mx-auto mt-3 max-w-xl text-sm text-slate-400 sm:text-base">{sub}</p>
      <div className="relative mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <a
          href={PHONE_HREF}
          data-testid="cta-call-button"
          className="flex w-full items-center justify-center gap-2 rounded-sm bg-amber-500 px-8 py-3.5 font-display text-base font-bold uppercase tracking-[0.15em] text-obsidian-950 transition-all duration-300 hover:bg-amber-400 active:scale-95 sm:w-auto"
        >
          <Phone className="h-4 w-4" /> {PHONE}
        </a>
        <a
          href={EMAIL_HREF}
          data-testid="cta-email-button"
          className="flex w-full items-center justify-center gap-2 rounded-sm border border-amber-500/50 px-8 py-3.5 font-display text-base font-bold uppercase tracking-[0.15em] text-amber-400 transition-all duration-300 hover:bg-amber-500/10 active:scale-95 sm:w-auto"
        >
          <Mail className="h-4 w-4" /> {EMAIL}
        </a>
      </div>
    </div>
  </Reveal>
);

export const PageHero = ({ eyebrow, title, sub, image, testId }) => (
  <section className="relative overflow-hidden border-b border-slate-800" data-testid={testId}>
    <div className="absolute inset-0">
      <img src={image} alt="" className="h-full w-full object-cover opacity-30" loading="eager" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,7,10,0.55) 0%, rgba(5,7,10,0.9) 75%, #05070A 100%)",
        }}
      />
    </div>
    <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-12">
      <Reveal>
        <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
          {eyebrow}
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h1
          className="mt-4 max-w-4xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          data-testid={`${testId}-title`}
        >
          {title}
        </h1>
      </Reveal>
      {sub ? (
        <Reveal delay={0.2}>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{sub}</p>
        </Reveal>
      ) : null}
    </div>
  </section>
);
